"use client";
import React, { useState, useEffect } from "react";
import { FiEye, FiEyeOff } from "react-icons/fi";
import axios from "axios";
import { message } from "antd";
import ContactInputStyled from "../Components/ContactInputStyled";

export default function AffiliateForm({
  isOpen,
  onClose,
  createAffiliate,
  userData,
}) {
  const [formData, setFormData] = useState({
    fullName: "",
    phoneNumber: "",
    email: "",
    password: "",
    dob: "",
    city: "",
    state: "",
    country: "",
    accountName: "",
    accountNumber: "",
    balance: "",
    brokerName: "",
    investorPassword: "",
    brokerServer: "",
    socialMedia: {
      facebook: "",
      linkedin: "",
      instagram: "",
      telegram: "",
      twitter: "",
    },
  });

  const [selectedSocialMedia, setSelectedSocialMedia] = useState([]);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [fieldErrors, setFieldErrors] = useState({});

  const handleSocialMediaChange = (platform, value) => {
    setFormData((prev) => ({
      ...prev,
      socialMedia: {
        ...prev.socialMedia,
        [platform]: value,
      },
    }));
  };

  const handleSocialMediaSelection = (platform) => {
    setSelectedSocialMedia((prev) => {
      const isSelected = prev.includes(platform);
      if (isSelected) {
        // Remove platform and clear its URL
        handleSocialMediaChange(platform, "");
        return prev.filter((item) => item !== platform);
      } else {
        // Add platform
        return [...prev, platform];
      }
    });
  };

  const socialMediaOptions = [
    { value: "facebook", label: "Facebook" },
    { value: "linkedin", label: "LinkedIn" },
    { value: "instagram", label: "Instagram" },
    { value: "telegram", label: "Telegram" },
    { value: "twitter", label: "Twitter" },
  ];

  // Define required fields
  const requiredFields = [
    "fullName",
    "phoneNumber",
    "email",
    "password",
    "dob",
    "city",
    "state",
    "country",
  ];

  // Validate required fields
  const validateForm = () => {
    const errors = [];
    const newFieldErrors = {};
    if (selectedSocialMedia.length === 0) {
      const errorMessage = "Please select at least one social media platform";
      errors.push(errorMessage);
      newFieldErrors.socialMedia = errorMessage;
    }

    // Validate URLs for selected social media platforms
    selectedSocialMedia.forEach((platform) => {
      if (
        !formData.socialMedia[platform] ||
        formData.socialMedia[platform].trim() === ""
      ) {
        const platformName =
          platform.charAt(0).toUpperCase() + platform.slice(1);
        const errorMessage = `${platformName} URL is required`;
        errors.push(errorMessage);
        newFieldErrors[`socialMedia_${platform}`] = errorMessage;
      }
    });

    setFieldErrors(newFieldErrors);
    return errors;
  };

  // Clean form data - remove empty non-required fields
  const cleanFormData = (data) => {
    const cleaned = { ...data };

    // Remove empty non-required fields
    Object.keys(cleaned).forEach((key) => {
      if (!requiredFields.includes(key) && key !== "socialMedia") {
        if (!cleaned[key] || cleaned[key].trim() === "") {
          delete cleaned[key];
        }
      }
    });

    if (cleaned.socialMedia) {
      const cleanedSocialMedia = {};
      selectedSocialMedia.forEach((platform) => {
        if (
          cleaned.socialMedia[platform] &&
          cleaned.socialMedia[platform].trim() !== ""
        ) {
          cleanedSocialMedia[platform] = cleaned.socialMedia[platform];
        }
      });

      if (Object.keys(cleanedSocialMedia).length > 0) {
        cleaned.socialMedia = cleanedSocialMedia;
      } else {
        delete cleaned.socialMedia;
      }
    }

    return cleaned;
  };

  // Reset form to initial state
  const resetForm = () => {
    setFormData({
      fullName: "",
      phoneNumber: "",
      email: "",
      password: "",
      dob: "",
      city: "",
      state: "",
      country: "",
      accountName: "",
      accountNumber: "",
      balance: "",
      brokerName: "",
      investorPassword: "",
      brokerServer: "",
      socialMedia: {
        facebook: "",
        linkedin: "",
        instagram: "",
        telegram: "",
        twitter: "",
      },
    });
    setSelectedSocialMedia([]);
    setFieldErrors({});
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const validationErrors = validateForm();
    if (validationErrors.length > 0) {
      message.error("Please fill in all required fields correctly.");
      setIsLoading(false);
      return;
    }
    setIsLoading(true);

    try {
      if (createAffiliate && userData?._id) {
        const socialMediaLinks = cleanFormData(formData).socialMedia || {};
        await createAffiliate(userData._id, socialMediaLinks);
      } else {
      }

      setTimeout(() => resetForm(), 500);
      setTimeout(() => onClose(), 1000);
    } catch (err) {
      console.error(err);
      message.error("Failed to create affiliate. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const handleEscapeKey = (e) => {
    if (e.key === "Escape") {
      onClose();
    }
  };

  useEffect(() => {
    if (isOpen) {
      document.addEventListener("keydown", handleEscapeKey);
      document.body.style.overflow = "hidden";
    } else {
      document.removeEventListener("keydown", handleEscapeKey);
      document.body.style.overflow = "unset";
    }

    return () => {
      document.removeEventListener("keydown", handleEscapeKey);
      document.body.style.overflow = "unset";
    };
  }, [isOpen]);

  if (!isOpen) return null;

  // Inline styles
  const styles = {
    modalOverlay: {
      position: "fixed",
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: "rgba(0, 0, 0, 0.8)",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      zIndex: 1000,
      padding: "20px",
    },
    modalContainer: {
      backgroundColor: "#0c121c",
      borderRadius: "12px",
      width: "100%",
      maxWidth: "900px",
      maxHeight: "90vh",
      overflow: "hidden",
      boxShadow: "0 25px 50px -12px rgba(31, 27, 27, 0.25)",
      border: "1px solid rgb(112, 112, 112)",
    },
    formWrapper: {
      padding: "30px",
      position: "relative",
      maxHeight: "calc(90vh - 80px)",
      overflowY: "auto",
      position: "relative",
      scrollbarWidth: "none", // Firefox
      msOverflowStyle: "none", // Internet Explorer 10+
    },
    formWrapperOpen: {
      padding: "30px",
      // maxHeight: "1000px",
      maxHeight: "calc(-80px + 90vh)",
      overflowY: "auto",
      position: "relative",
      scrollbarWidth: "none",
      msOverflowStyle: "none",
    },
    closeButton: {
      position: "absolute",
      top: "20px",
      right: "20px",
      background: "none",
      border: "none",
      fontSize: "24px",
      color: "#fff",
      cursor: "pointer",
      width: "32px",
      height: "32px",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      borderRadius: "50%",
      transition: "background-color 0.2s",
    },
    title: {
      fontSize: "28px",
      fontWeight: "700",
      color: "#fff000",
      textAlign: "center",
      marginBottom: "30px",
      background: "transparent",
      WebkitBackgroundClip: "text",
      backgroundClip: "text",
    },
    form: {
      display: "flex",
      flexDirection: "column",
      gap: "25px",
    },
    formGrid: {
      display: "grid",
      gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))",
      gap: "20px",
    },
    inputGroup: {
      display: "flex",
      flexDirection: "column",
      gap: "8px",
    },
    label: {
      fontSize: "14px",
      fontWeight: "600",
      color: "#e2e8f0",
      display: "flex",
      alignItems: "center",
      gap: "4px",
    },
    required: {
      color: "#e94560",
      fontSize: "16px",
    },
    input: {
      padding: "12px 16px",
      borderRadius: "8px",
      border: "1px solid #374151",
      backgroundColor: "#404246",
      color: "#ffffff",
      fontSize: "14px",
      transition: "all 0.2s ease",
      outline: "none",
    },
    inputError: {
      // Remove red border styling - keep input appearance consistent
    },
    passwordWrapper: {
      position: "relative",
      display: "grid",
      alignItems: "center",
    },
    passwordToggle: {
      position: "absolute",
      right: "12px",
      background: "none",
      border: "none",
      color: "#9ca3af",
      cursor: "pointer",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      padding: "4px",
    },
    fieldError: {
      color: "#e94560",
      fontSize: "12px",
      marginTop: "4px",
    },
    socialMediaSection: {
      marginTop: 0,
    },
    dropdownContainer: {
      position: "relative",
    },
    dropdownButton: {
      width: "100%",
      padding: "12px 16px",
      borderRadius: "8px",
      border: "1px solid #374151",
      backgroundColor: "#404246",
      color: "#ffffff",
      fontSize: "14px",
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      cursor: "pointer",
      transition: "all 0.2s ease",
    },
    dropdownText: {
      textAlign: "left",
    },
    dropdownArrow: {
      transition: "transform 0.2s ease",
      fontSize: "12px",
    },
    dropdownArrowOpen: {
      transform: "rotate(180deg)",
    },
    dropdownMenu: {
      position: "relative",
      top: "100%",
      left: 0,
      right: 0,
      backgroundColor: "transparent",
      borderRadius: "8px",
      zIndex: 10,
      maxHeight: "230px",
      overflowY: "auto",
      height: "0",
      transition: "0.4s ease-in-out",
    },
    dropdownMenuopen: {
      backgroundColor: "#404246",
      borderColor: "#374151",
      height: "217px",
      marginTop: "4px",
    },
    dropdownOption: {
      display: "flex",
      alignItems: "center",
      gap: "12px",
      padding: "12px 16px",
      cursor: "pointer",
      transition: "background-color 0.2s",
      fontSize: "14px",
      color: "#ffffff",
    },
    checkbox: {
      display: "none",
    },
    checkboxCustom: {
      width: "16px",
      height: "16px",
      border: "2px solid #374151",
      borderRadius: "3px",
      display: "flex",
      color: "#000",
      alignItems: "center",
      justifyContent: "center",
      backgroundColor: "#fff",
      transition: "all 0.2s ease",
    },
    socialMediaUrls: {
      marginTop: "16px",
      display: "grid",
      gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))",
      gap: "20px",
    },

    sectionHeader: {
      marginTop: "5px",
    },
    sectionTitle: {
      fontSize: "20px",
      fontWeight: "600",
      color: "#ffffff",
      marginBottom: "8px",
    },
    sectionSubtitle: {
      fontSize: "14px",
      color: "#9ca3af",
      lineHeight: "1.5",
    },
    submitButtonContainer: {
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      position: "sticky",
    },
    submitButton: {
      padding: "14px",
      width: "30%",
      borderRadius: "8px",
      border: "none",
      background: "#fff000",
      color: "#000",
      fontSize: "16px",
      fontWeight: "600",
      cursor: "pointer",
      transition: "all 0.2s ease",
      marginTop: "20px",
      disabled: {
        opacity: 0.6,
        cursor: "not-allowed",
      },
    },
  };

  return (
    <div style={styles.modalOverlay} onClick={handleOverlayClick}>
      <div style={styles.modalContainer}>
        <div
          style={isDropdownOpen ? styles.formWrapperOpen : styles.formWrapper}
        >
          <button
            style={{
              ...styles.closeButton,
              ":hover": { backgroundColor: "rgba(233, 69, 96, 0.1)" },
            }}
            onClick={onClose}
            aria-label="Close modal"
          >
            ×
          </button>
          <h1 style={styles.title}>Affiliate Request</h1>

          <form onSubmit={handleSubmit} style={styles.form}>
            <div style={styles.socialMediaSection}>
              <div style={styles.inputGroup}>
                <label style={styles.label}>Social Media Platforms</label>
                <div style={styles.dropdownContainer}>
                  <button
                    type="button"
                    style={styles.dropdownButton}
                    onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                  >
                    <span style={styles.dropdownText}>
                      {selectedSocialMedia.length === 0
                        ? "Select social media platforms"
                        : `${selectedSocialMedia.length} platform${
                            selectedSocialMedia.length > 1 ? "s" : ""
                          } selected`}
                    </span>
                    <span
                      style={{
                        ...styles.dropdownArrow,
                        ...(isDropdownOpen ? styles.dropdownArrowOpen : {}),
                      }}
                    >
                      ▼
                    </span>
                  </button>
                  {fieldErrors.socialMedia && (
                    <span style={styles.fieldError}>
                      {fieldErrors.socialMedia}
                    </span>
                  )}

                  {/* {isDropdownOpen && ( */}
                  <div
                    style={{
                      ...styles.dropdownMenu,
                      ...(isDropdownOpen && styles.dropdownMenuopen),
                    }}
                  >
                    {socialMediaOptions.map((option) => (
                      <label
                        key={option.value}
                        style={{
                          ...styles.dropdownOption,
                          ":hover": { backgroundColor: "#1e293b" },
                        }}
                      >
                        <input
                          type="checkbox"
                          checked={selectedSocialMedia.includes(option.value)}
                          onChange={() =>
                            handleSocialMediaSelection(option.value)
                          }
                          style={styles.checkbox}
                        />
                        <span
                          style={{
                            ...styles.checkboxCustom,
                            backgroundColor: selectedSocialMedia.includes(
                              option.value
                            )
                              ? "#fff000"
                              : "#191a1c",
                            borderColor: selectedSocialMedia.includes(
                              option.value
                            )
                              ? "#fff000"
                              : "#191a1c",
                          }}
                        >
                          {selectedSocialMedia.includes(option.value) && "✓"}
                        </span>
                        <span>{option.label}</span>
                      </label>
                    ))}
                  </div>
                  {/* )} */}
                </div>
              </div>

              {selectedSocialMedia.length > 0 && (
                <div style={styles.socialMediaUrls}>
                  {selectedSocialMedia.map((platform) => {
                    const option = socialMediaOptions.find(
                      (opt) => opt.value === platform
                    );
                    return (
                      <div key={platform} style={styles.inputGroup}>
                        <label style={styles.label}>
                          <span style={styles.required}>*</span>
                          {option.label} URL
                        </label>
                        <input
                          type="url"
                          value={formData.socialMedia[platform]}
                          onChange={(e) =>
                            handleSocialMediaChange(platform, e.target.value)
                          }
                          placeholder={`Enter your ${option.label} profile URL`}
                          style={{
                            ...styles.input,
                            ...(fieldErrors[`socialMedia_${platform}`]
                              ? styles.inputError
                              : {}),
                          }}
                        />
                        {fieldErrors[`socialMedia_${platform}`] && (
                          <span style={styles.fieldError}>
                            {fieldErrors[`socialMedia_${platform}`]}
                          </span>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            <div style={styles.submitButtonContainer}>
              <button
                type="submit"
                style={{
                  ...styles.submitButton,
                  ...(isLoading ? styles.submitButton.disabled : {}),
                }}
                disabled={isLoading}
              >
                {isLoading ? "Registering..." : "Register Now"}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
